import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";

export type ActivityType = "note" | "call" | "meeting" | "email" | "whatsapp";

export interface ActivityPreset {
  id: string;
  type: ActivityType | string;
  label: string;
  outcome: string | null;
  suggestedNextStatus: string | null;
  sortOrder: number;
}

export interface ActivityRow {
  id: string;
  type: string;
  presetId: string | null;
  outcome: string | null;
  body: string | null;
  payload: unknown;
  actorUserId: string | null;
  occurredAt: string;
  createdAt: string;
}

interface PresetsResponse { presets: ActivityPreset[] }
interface ListResponse { activities: ActivityRow[] }

export function usePresetsQuery(workspaceSlug: string | null) {
  return useQuery({
    enabled: !!workspaceSlug,
    queryKey: ["activity-presets", workspaceSlug],
    queryFn: () =>
      apiClient.get<PresetsResponse>("/api/activities/presets", {
        params: { workspace: workspaceSlug ?? undefined },
      }),
    staleTime: 5 * 60 * 1000,
  });
}

export interface LastActivity {
  id: string;
  type: string;
  outcome: string | null;
  body: string | null;
  actorUserId: string | null;
  occurredAt: string;
}

// Lazy hover fetch for the lead-list "last activity" chip. Only enabled once a
// row is actually hovered, so the single LIMIT-1 endpoint is hit for inspected
// rows rather than every row in the list.
export function useLastActivityQuery(
  workspaceSlug: string | null,
  leadId: string | undefined,
  enabled: boolean
) {
  return useQuery({
    enabled: !!workspaceSlug && !!leadId && enabled,
    queryKey: ["activities", workspaceSlug, leadId, "last"],
    queryFn: () =>
      apiClient.get<{ activity: LastActivity | null }>(
        `/api/activities/lead/${leadId}/last`,
        { params: { workspace: workspaceSlug ?? undefined } }
      ),
    staleTime: 60_000,
  });
}

export function useActivitiesQuery(workspaceSlug: string | null, leadId: string | undefined) {
  return useQuery({
    enabled: !!workspaceSlug && !!leadId,
    queryKey: ["activities", workspaceSlug, leadId],
    queryFn: () =>
      apiClient.get<ListResponse>(`/api/activities/lead/${leadId}`, {
        params: { workspace: workspaceSlug ?? undefined },
      }),
  });
}

export interface LogActivityArgs {
  leadId: string;
  type: ActivityType;
  presetId?: string;
  body?: string;
  outcome?: string;
  // Optional follow-up date (YYYY-MM-DD, Asia/Kolkata) set alongside the log.
  followUpAt?: string;
  applySuggestedStatus?: boolean;
}

export function useLogActivity(workspaceSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LogActivityArgs) =>
      apiClient.post<{ activity: ActivityRow }>("/api/activities", {
        params: { workspace: workspaceSlug ?? undefined },
        json: input,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["activities", workspaceSlug, vars.leadId] });
      void qc.invalidateQueries({ queryKey: ["leads", workspaceSlug] });
    },
  });
}

export interface ChangeStatusArgs {
  leadId: string;
  status: string;
  reason?: string;
}

export function useChangeLeadStatus(workspaceSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangeStatusArgs) =>
      apiClient.post(`/api/leads/${input.leadId}/status`, {
        params: { workspace: workspaceSlug ?? undefined },
        json: { status: input.status, ...(input.reason ? { reason: input.reason } : {}) },
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["leads", workspaceSlug] });
      void qc.invalidateQueries({ queryKey: ["activities", workspaceSlug, vars.leadId] });
    },
  });
}
